import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Briefcase, ChevronRight, Loader2, Settings, Trash2 } from "lucide-react";
import { INSTANCE_ICONS, INSTANCE_COLORS, DEFAULT_ICON, DEFAULT_COLOR, formatGoal } from "./workspaceModel";

const Field = ({ label, children }) => (
  <div>
    <label className="text-sm text-gray-400 mb-2 block">{label}</label>
    {children}
  </div>
);

// Step 1 of creating a workspace: name, icon and colour. Onboarding follows on Continue.
export const NewWorkspaceModal = ({ open, onOpenChange, creating, onCreate }) => {
  const [name, setName] = useState("");
  const [icon, setIcon] = useState(DEFAULT_ICON);
  const [color, setColor] = useState(DEFAULT_COLOR);

  useEffect(() => {
    if (open) { setName(""); setIcon(DEFAULT_ICON); setColor(DEFAULT_COLOR); }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0a] border-gray-800 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl text-white flex items-center gap-2"><Briefcase className="h-5 w-5 text-ism-teal" />New Business Initiative</DialogTitle>
          <DialogDescription className="text-gray-400">Create a workspace for a new business, product line, or sales initiative.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <Field label="Workspace Name">
            <Input placeholder="e.g., Acme Corp - Enterprise, StartupX - Seed Round" value={name} onChange={(e) => setName(e.target.value)}
              className="bg-white/5 border-gray-700 text-white" autoFocus />
          </Field>
          <Field label="Icon">
            <div className="flex flex-wrap gap-2">
              {INSTANCE_ICONS.map((i) => (
                <button key={i} onClick={() => setIcon(i)}
                  className={`w-10 h-10 rounded-lg flex items-center justify-center text-xl transition-all ${icon === i ? "bg-ism-teal/20 ring-2 ring-ism-teal" : "bg-white/5 hover:bg-white/10"}`}>
                  {i}
                </button>
              ))}
            </div>
          </Field>
          <Field label="Color">
            <div className="flex flex-wrap gap-2">
              {INSTANCE_COLORS.map((c) => (
                <button key={c} onClick={() => setColor(c)} style={{ backgroundColor: c }}
                  className={`w-8 h-8 rounded-full transition-all ${color === c ? "ring-2 ring-white ring-offset-2 ring-offset-[#0a0a0a]" : ""}`} />
              ))}
            </div>
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-gray-700 text-gray-300">Cancel</Button>
          <Button onClick={() => onCreate({ name, icon, color })} disabled={creating || !name.trim()}
            className="bg-ism-teal/20 text-ism-teal border border-ism-teal/30 hover:bg-ism-teal/30">
            {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ChevronRight className="h-4 w-4 mr-2" />}
            Continue to Setup
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const WorkspaceSettingsModal = ({ open, onOpenChange, instance, onDelete }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-[#0a0a0a] border-gray-800 text-white max-w-md">
      <DialogHeader>
        <DialogTitle className="text-xl text-white flex items-center gap-2"><Settings className="h-5 w-5 text-ism-teal" />Workspace Settings</DialogTitle>
      </DialogHeader>
      {instance && (
        <div className="space-y-4 py-4">
          <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center text-2xl" style={{ backgroundColor: `${instance.color}20` }}>{instance.icon}</div>
            <div>
              <p className="text-white font-normal">{instance.name}</p>
              <p className="text-sm text-gray-500">{instance.companyName || "No company set"}</p>
            </div>
          </div>
          {instance.revenueGoal && (
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-white/5 rounded-lg">
                <p className="text-xs text-gray-500">Revenue Goal</p>
                <p className="text-lg text-white">{formatGoal(instance.revenueGoal)}</p>
              </div>
              <div className="p-3 bg-white/5 rounded-lg">
                <p className="text-xs text-gray-500">Current</p>
                <p className="text-lg text-ism-teal">{formatGoal(instance.currentRevenue || 0)}</p>
              </div>
            </div>
          )}
          <div className="pt-4 border-t border-gray-800">
            <Button variant="destructive" onClick={() => onDelete(instance.instanceId)}
              className="w-full bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20">
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Workspace
            </Button>
            <p className="text-xs text-gray-500 mt-2 text-center">This will permanently delete all data in this workspace.</p>
          </div>
        </div>
      )}
    </DialogContent>
  </Dialog>
);
